import styled from "styled-components"
import { FlexTable } from "@common/components/table/FlexTable"
import { useStore } from "@state/store"
import useSortConfig from "@common/hooks/useSortConfig"
import { useEffect, useRef } from "react"
import gsap from "gsap"
import { useProjectDataStore } from "@state/projectDataStore"
import TextButton from "@common/ui/TextButton"
import Filters from "@common/components/ResidenceGeneric/Filters"
import ResidencePopup from "@common/components/ResidenceGeneric/ResidencePopup"
import { useCanvasStore } from "@state/canvasStore"
import { CAM_POSITION_INDEX } from "@data/cameraData"
import { HeartIcon } from "@radix-ui/react-icons"
import useLightControl from "@utils/useLightsControl"

const ResidencesType2 = () => {
  const { residencesData } = useProjectDataStore((s) => ({
    residencesData: s.residencesData,
    availabilityData: s.availabilityData,
    typeData: s.typeData,
    brandingData: s.brandingData,
    setLocalState: s.setLocalState,
  }))
  const { sortConfig, setSortConfig } = useSortConfig()

  const controlsRef = useRef(null)
  const syncState = useStore((s) => s.syncState)
  const controlsVisible = useStore((s) => s.controlsVisible)
  const selectedUnit = useStore((s) => s.selectedUnit)
  const selectedPhase = useStore((s) => s.selectedPhase)
  const residencePopup = useStore((s) => s.residencePopup)
  const filteredUnits = useStore((s) => s.filteredUnits)
  const setCameraData = useCanvasStore((s) => s.setCameraData)

  const categories = residencesData?.categories

  const activeUnits = filteredUnits?.filter((unit: any) => unit.active)
  const unitsToLight =
    activeUnits?.map((unit: any) => ({
      Name: unit.unitName?.slice(1),
    })) || []

  useLightControl(selectedUnit, unitsToLight)

  useEffect(() => {
    gsap.to(controlsRef.current, {
      transform: controlsVisible ? "translateX(0)" : "translateX(-100%)",
      ease: "power2.inOut",
      direction: 0.6,
    })

    if (controlsVisible) {
      if (selectedPhase === "phase1") setCameraData(CAM_POSITION_INDEX.corner)
      else if (selectedPhase === "phase2")
        setCameraData(CAM_POSITION_INDEX.phase2)
    } else if (!controlsVisible) {
      setCameraData(CAM_POSITION_INDEX.wide)
    }
  }, [controlsVisible, selectedPhase])

  useEffect(() => {
    syncState({
      contentControls: {
        categories: categories,
        index: 0,
      },
    })

    return () => {
      syncState({
        selectedUnit: null,
        residencePopup: false,
        contentControls: null,
      })
    }
  }, [])

  const headers = [
    "Unit",
    "price",
    "floor",
    "beds",
    <HeartIcon className="fav-icon" />,
  ]

  const cells = ["id", "price", "floor", "beds"]

  useEffect(() => {
    console.log("residencePopup changed:", residencePopup)
  }, [residencePopup])

  return (
    <Wrapper>
      <ControlsWrapper ref={controlsRef}>
        <TextButton
          onClick={() =>
            syncState({ controlsVisible: !controlsVisible, selectedUnit: null })
          }
          icon={
            controlsVisible
              ? "/assets/icons/cross-black.svg"
              : "/assets/icons/right-arrow-black.svg"
          }
          color="accent"
          className="toggleIcon"
        ></TextButton>
        <FilterContainer>
          <Filters className="filters" />
        </FilterContainer>
        <TableContainer>
          <FlexTable
            className="table"
            uniqueKey="unitName"
            headers={headers}
            selection="single"
            cells={cells}
            data={filteredUnits?.filter((unit) => unit.active) || []}
            sortConfig={sortConfig}
            setSortConfig={setSortConfig}
            initialSelectedRows={selectedUnit || []} // Pass selectedUnit as initialSelectedRows
            onSelectionChange={(rows) => {
              syncState({
                selectedUnit: rows,
              })
            }}
          />
        </TableContainer>
        <TextButton
          onClick={() => setCameraData(CAM_POSITION_INDEX.start)}
          icon="/assets/icons/enlarge.png"
          color="accent"
          className="exploreUnit"
        />
      </ControlsWrapper>
      {residencePopup && <ResidencePopup selectedUnit={selectedUnit} />}
    </Wrapper>
  )
}

export default ResidencesType2

const ControlsWrapper = styled.div`
  background-color: #7f9089f5;
  width: 27vw;
  border-right: #00000092 solid 2px;
  height: 100%;
  display: flex;
  align-items: center;
  pointer-events: all;
  flex-direction: column;
  padding: 2rem;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 0;
  transform: translateX(-100%);

  .toggleIcon {
    top: 2rem;
    position: absolute;
    right: 0;
    transform: translateX(100%);
    z-index: 2;
    border-radius: 0 100% 100% 0;
  }

  .exploreUnit {
    top: 8rem;
    position: absolute;
    right: 0;
    transform: translateX(100%);
    z-index: 2;
    border-radius: 0 100% 100% 0;
  }
`

const Wrapper = styled.div`
  z-index: 3;
  height: 100%;
  background-color: aquamarine;
  pointer-events: none;
  overflow: hidden;

  .explore {
    z-index: 10;
    position: absolute;
    bottom: 0;
    right: 0;

    margin: 2rem;
    color: white;
    cursor: pointer;
    border-radius: 2rem;

    &:active {
      transform: scale(0.98);
      opacity: 0.5;
    }
  }
`

const FilterContainer = styled.div`
  width: 100%;
  height: 40%;
  border: #ffffff50 solid 1px;
`

const TableContainer = styled.div`
  width: 100%;
  /* border: 1px solid red; */
  height: 55%;
`
